import * as Haptics from 'expo-haptics';
import i18n from '@/utils/i18n';
import MinusIcon from '@assets/icons/minus.svg';
import PlusIcon from '@assets/icons/plus.svg';
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TrashIcon from '@assets/icons/trash-small.svg';
import { ActivityIndicator } from './activity-indicator';
import { ApplicationType } from '@/contexts/dynamic-config';
import {
  TextInput as BaseTextInput,
  InteractionManager,
  PixelRatio,
  Platform,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { BlurView } from 'expo-blur';
import {
  createMinFloatValidator,
  TextInput,
  validationFunctions,
  Validations,
} from './text-input';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING, SCREEN_PADDING_XS, TYPOGRAPHY } from '@/styles';
import {
  handleWebKeyPress,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useMaybeAuthStackContext } from '@/contexts/auth-stack-context';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

export enum ZeroQuantityBehavior {
  CLOSED = 'CLOSED',
  LOADING = 'LOADING',
}

enum QuantitySelectorVariant {
  BLURRED = 'blurred',
  DEFAULT = 'default',
}

export interface Props {
  additionalIcon?: FunctionComponent<{
    height: number;
    stroke: string;
    width: number;
  }> | null;
  canDecrementToZero?: boolean;
  decrementDisabled?: boolean;
  disabled?: boolean;
  disableTextInput?: boolean;
  incrementDisabled?: boolean;
  inDraggableSheet?: boolean;
  isFocused: boolean;
  itemDisplayName?: string; // For accessibility labels
  loading?: boolean;
  maxQuantity?: number | null;
  minQuantity?: number | null;
  onChange: (previousQuantity: number, quantity: number) => void;
  onChangeText?: (quantity: string) => void;
  onNavigateToBecomeCustomer?: () => void;
  quantity: number;
  quantityPillStyle?: StyleProp<TextStyle>;
  showTooltipOnMount?: boolean;
  textInputStyle?: StyleProp<ViewStyle>;
  variant?: 'default' | 'blurred';
  zeroQuantityBehavior?: ZeroQuantityBehavior;
}

const fontScale = PixelRatio.getFontScale();

export const DECREMENT_HIT_SLOP = {
  bottom: SCREEN_PADDING_XS,
  left: SCREEN_PADDING,
  right: 0,
  top: SCREEN_PADDING_XS,
};

export const INCREMENT_HIT_SLOP = {
  bottom: SCREEN_PADDING_XS,
  left: 0,
  right: SCREEN_PADDING,
  top: SCREEN_PADDING_XS,
};

export const INPUT_HIT_SLOP = {
  bottom: 12,
  left: 0,
  right: 0,
  top: 12,
};

export const TEXT_INPUT_CONTAINER_SPACING = 8;
export const ORDER_ITEM_QUANTITY_HEIGHT = 32 * fontScale;
export const QUANTITY_INPUT_WIDTH = ORDER_ITEM_QUANTITY_HEIGHT;
export const EXPANDED_QUANTITY_WIDTH =
  ORDER_ITEM_QUANTITY_HEIGHT * 3 + TEXT_INPUT_CONTAINER_SPACING;

const ICON_SIZE = 16 * fontScale;
export const TRASH_ICON_SIZE = 14 * fontScale;

const ADDITIONAL_ITEM_DIMENSION = 18 * fontScale;
const ADDITIONAL_ICON_HEIGHT = Math.ceil(10 * fontScale);
const ADDITIONAL_ICON_WIDTH = Math.ceil(3 * fontScale);

const inputValidations = [
  validationFunctions[Validations.INTEGER],
  validationFunctions[Validations.POSITIVE],
];

const canNotDecrementToZeroValidators = [
  ...inputValidations,
  createMinFloatValidator(1),
];

// Arbitrary high limit to prevent update cart errors
const QUANTITY_LIMIT = 1000000;

const getMinMaxDisplayString = (
  minString: string | null,
  maxString: string | null,
) => {
  return [minString, maxString].filter(Boolean).join(', ');
};

export const QuantitySelector: React.FC<Props> = ({
  additionalIcon,
  canDecrementToZero = true,
  decrementDisabled,
  disabled = false,
  disableTextInput = false,
  incrementDisabled,
  inDraggableSheet = false,
  isFocused,
  itemDisplayName,
  loading,
  maxQuantity,
  minQuantity,
  onChange,
  onChangeText,
  onNavigateToBecomeCustomer,
  quantity,
  quantityPillStyle,
  showTooltipOnMount = false,
  textInputStyle,
  zeroQuantityBehavior = ZeroQuantityBehavior.CLOSED,
  variant = QuantitySelectorVariant.DEFAULT,
}) => {
  const [styles, theme] = useStyles();
  const { isGuestMode } = useUserContext();
  const { showErrorToast } = useToasts();
  const authStackContext = useMaybeAuthStackContext();
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;
  const [displayText, setDisplayText] = useState(String(quantity));
  const [openForFocus, setOpenForFocus] = useState(false);
  const [autoFocusInput, setAutoFocusInput] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(showTooltipOnMount);

  const mouseDownRef = useRef(false);
  const inputRef = useRef<BaseTextInput>(null);
  const displayTextRef = useRef(displayText);
  const quantityRef = useRef(quantity);
  const changeFromTextInputRef = useRef<(textValue: string) => void>(null);
  const hasUncommittedTextRef = useRef(false);

  const isTrivialMinQuantityCase = minQuantity === 0 || minQuantity === 1;
  const isAboveOrAtMaxQuantity = Boolean(
    typeof maxQuantity === 'number' && quantity >= maxQuantity,
  );
  const isBelowOrAtMinQuantity = Boolean(
    !isTrivialMinQuantityCase &&
      typeof minQuantity === 'number' &&
      quantity <= minQuantity &&
      quantity > 0,
  );

  const minString =
    typeof minQuantity === 'number' && !isTrivialMinQuantityCase
      ? i18n.t('quantity.min', { minQuantity })
      : null;
  const maxString =
    typeof maxQuantity === 'number'
      ? i18n.t('quantity.max', { maxQuantity })
      : null;

  const quantityTextString = useMemo(
    () => getMinMaxDisplayString(minString, maxString),
    [minString, maxString],
  );
  const isBreachingQuantityRestriction =
    isBelowOrAtMinQuantity || isAboveOrAtMaxQuantity;

  const [tooltipText, setTooltipText] = useState(() => {
    if (!isBreachingQuantityRestriction) {
      return '';
    }
    if (isBelowOrAtMinQuantity && isAboveOrAtMaxQuantity) {
      return getMinMaxDisplayString(minString, maxString);
    }
    if (isBelowOrAtMinQuantity) {
      return minString;
    }

    return maxString;
  });

  useEffect(() => {
    if (hasInteracted && isBreachingQuantityRestriction) {
      if (displayText) {
        const displayTextNumber = Number(displayText);

        const showMinString =
          typeof minQuantity === 'number' &&
          displayTextNumber <= minQuantity &&
          !isTrivialMinQuantityCase;

        const showMaxString =
          typeof maxQuantity === 'number' && displayTextNumber >= maxQuantity;

        if (!showMinString && !showMaxString) {
          setTooltipText('');
          return;
        }

        if (showMinString && showMaxString) {
          setTooltipText(getMinMaxDisplayString(minString, maxString));
        } else if (showMinString) {
          setTooltipText(minString);
        } else {
          setTooltipText(maxString);
        }

        const timer = setTimeout(() => {
          setTooltipText('');
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        setTooltipText('');
      }
    } else {
      setTooltipText('');
    }
  }, [
    displayText,
    hasInteracted,
    isBreachingQuantityRestriction,
    isTrivialMinQuantityCase,
    maxQuantity,
    maxString,
    minQuantity,
    minString,
  ]);

  useEffect(() => {
    hasUncommittedTextRef.current = false;
    setDisplayText(String(quantity));
  }, [quantity]);

  useEffect(() => {
    if (isFocused) {
      setOpenForFocus(true);
      setAutoFocusInput(true);

      if (Platform.OS === 'web') {
        // Use requestAnimationFrame on web for more reliable focus timing
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      } else {
        InteractionManager.runAfterInteractions(() => {
          inputRef.current?.focus();
        });
      }
    } else {
      setOpenForFocus(false);
      setAutoFocusInput(false);
    }
  }, [isFocused]);

  const onChangeCallback = (nextQuantity: number) => {
    if (nextQuantity !== quantity) {
      onChange(quantity, nextQuantity);
    }
  };

  const updateQuantity = (nextQuantity: number) => {
    hasUncommittedTextRef.current = false;
    setDisplayText(String(nextQuantity));
    onChangeCallback(nextQuantity);

    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
  };

  const decrementQuantity = () => {
    setHasInteracted(true);
    let nextQuantity = quantity - 1;

    // If the minimum quantity has been met or exceeded, set the quantity to 0
    if (isBelowOrAtMinQuantity) {
      updateQuantity(0);
    } else {
      // Otherwise, decrement the quantity by 1, ensuring it doesn't go below 0 or 1 based on canDecrementToZero
      updateQuantity(Math.max(nextQuantity, canDecrementToZero ? 0 : 1));
    }
  };

  const incrementQuantity = () => {
    setHasInteracted(true);
    let nextQuantity = quantity + 1;

    // If the minimum quantity is defined and not 0, and the current quantity is 0, set the incremented quantity to the minimum quantity
    if (typeof minQuantity === 'number' && quantity === 0) {
      nextQuantity = minQuantity;
    } else if (isAboveOrAtMaxQuantity) {
      // If the maximum quantity has been met or exceeded, do nothing
      return;
    }

    updateQuantity(nextQuantity);
  };

  const changeFromTextInput = (textValue: string) => {
    setHasInteracted(true);
    setDisplayText(textValue);
    let nextQuantity = textValue
      ? Math.max(Number(textValue), canDecrementToZero ? 0 : 1)
      : canDecrementToZero
        ? 0
        : quantity;

    const minQuantityExceeded =
      typeof minQuantity === 'number' &&
      nextQuantity < minQuantity &&
      nextQuantity !== 0;
    const maxQuantityExceeded =
      typeof maxQuantity === 'number' && nextQuantity > maxQuantity;

    if (minQuantityExceeded) {
      nextQuantity = minQuantity; // Set to min limit if breached
    } else if (maxQuantityExceeded) {
      nextQuantity = maxQuantity; // Set to max limit if breached
    }

    if (nextQuantity >= QUANTITY_LIMIT) {
      nextQuantity = quantity;
      showErrorToast(
        i18n.t('toast.invalid_order_item_quantity', {
          quantity: Number(textValue),
        }),
      );
    }
    onChangeCallback(nextQuantity);
    hasUncommittedTextRef.current = false;
    setDisplayText(String(nextQuantity));
  };

  // Ensure text input quantity changes are committed when the component unmounts.
  // Only fire if there is uncommitted text from the user.
  // This is to avoid bugs caused by onBlur / onEndEditing not being called.
  // Keep refs in sync for the unmount cleanup effect
  displayTextRef.current = displayText;
  quantityRef.current = quantity;
  changeFromTextInputRef.current = changeFromTextInput;
  useEffect(() => {
    return () => {
      if (
        hasUncommittedTextRef.current &&
        displayTextRef.current !== String(quantityRef.current)
      ) {
        changeFromTextInputRef.current?.(displayTextRef.current);
      }
    };
  }, []);

  const isOpen =
    !isGuestMode &&
    (openForFocus ||
      quantity > 0 ||
      zeroQuantityBehavior === ZeroQuantityBehavior.LOADING);

  const isDecrementButtonDisabled =
    !canDecrementToZero && (quantity === 1 || quantity === minQuantity);

  useEffect(() => {
    if (!isOpen) {
      setAutoFocusInput(false);
    }
  }, [isOpen]);

  const removalLoading =
    quantity === 0 && zeroQuantityBehavior === ZeroQuantityBehavior.LOADING;
  const showLoadingIndicator = removalLoading || loading;

  const decrementComponent = showLoadingIndicator ? (
    <ActivityIndicator
      color={theme.colors.accentText}
      size="small"
      testID="activity-indicator"
    />
  ) : (quantity === 1 && canDecrementToZero) || isBelowOrAtMinQuantity ? (
    <TrashIcon
      height={TRASH_ICON_SIZE}
      stroke={theme.colors.accentText}
      testID="trash-icon"
      width={TRASH_ICON_SIZE}
    />
  ) : (
    <MinusIcon
      fill={theme.colors.accentText}
      height={ICON_SIZE}
      testID="minus-icon"
      width={ICON_SIZE}
    />
  );

  if (isCatalogLimited) {
    return null;
  }

  /*
   * To make tab navigation easier on the web, the + / - buttons don't maintain focus
   * The - button is removed from being focusable. If in a collapsed state, the + button
   * can be focused, but will immediately transfer focus to the text input.
   * `onFocus` is called before `onPress` for the + button, so we use mouse down / up
   * to disable the press focus from opening the text input before we actually change
   * the quantity.
   * Ticket details: https://gitlab.com/usepepper/product/-/issues/1607
   */

  const content = (
    <>
      {isOpen ? (
        <View style={styles.container}>
          <TouchableOpacity
            accessibilityLabel={i18n.t('decrease_quantity')}
            accessibilityRole="button"
            disabled={disabled || decrementDisabled || loading}
            focusable={false}
            hitSlop={DECREMENT_HIT_SLOP}
            // Disable the onPress callback instead of using the `disabled` prop since
            // the click event would propagate and open the item details sheet
            onPress={isDecrementButtonDisabled ? undefined : decrementQuantity}
            style={[
              styles.quantityButton,
              isDecrementButtonDisabled || disabled
                ? styles.quantityButtonDisabled
                : null,
            ]}
            testID="decrement-quantity"
          >
            {decrementComponent}
          </TouchableOpacity>
          <TextInput
            accessibilityLabel={
              itemDisplayName
                ? i18n.t('quantity_input_for_item', {
                    itemName: itemDisplayName,
                    quantity,
                  })
                : i18n.t('quantity_input', { quantity })
            }
            autoFocus={autoFocusInput}
            containerStyle={[styles.textInputContainer, textInputStyle]}
            editable={
              !disableTextInput &&
              !disabled &&
              !showLoadingIndicator &&
              !loading
            }
            hitSlop={INPUT_HIT_SLOP}
            inDraggableSheet={inDraggableSheet}
            inputRef={inputRef}
            keyboardType="number-pad"
            onBlur={
              Platform.OS === 'web'
                ? (event) => {
                    changeFromTextInput(event.nativeEvent.text);
                    setOpenForFocus(false);
                  }
                : undefined
            }
            onChangeText={(text) => {
              setHasInteracted(true);
              hasUncommittedTextRef.current = true;
              setDisplayText(text);
              onChangeText?.(text);
            }}
            onEndEditing={
              Platform.OS === 'web'
                ? undefined
                : () => {
                    changeFromTextInput(displayText);
                  }
            }
            onKeyPress={(e) => {
              setHasInteracted(true);
              handleWebKeyPress(e);
              /**
               * since ArrowUp/ ArrowDown are used to increment/decrement quantity,
               * and clash with our global shortcuts,
               * we need to prevent the default behavior of the input
               * this is akin to checking to see if alt is pressed
               */
              if (
                typeof e.isDefaultPrevented === 'function' &&
                e.isDefaultPrevented()
              ) {
                return;
              }

              switch (e.nativeEvent.key) {
                case Key.ArrowUp:
                  incrementQuantity();
                  break;
                case Key.ArrowDown:
                  decrementQuantity();
                  break;
                case Key.Escape:
                  inputRef.current?.blur();
                  break;
              }
            }}
            selectTextOnFocus
            showDoneButtonAccessoryView
            style={[TYPOGRAPHY.small, styles.input]}
            testID="quantity-input"
            validators={
              canDecrementToZero
                ? inputValidations
                : canNotDecrementToZeroValidators
            }
            value={displayText}
          />
        </View>
      ) : null}
      <TouchableOpacity
        accessibilityLabel={i18n.t('increase_quantity')}
        accessibilityRole="button"
        disabled={disabled || incrementDisabled || loading}
        focusable={!isOpen}
        hitSlop={INCREMENT_HIT_SLOP}
        onFocus={() => {
          if (!mouseDownRef.current && !disabled) {
            setOpenForFocus(true);

            InteractionManager.runAfterInteractions(() => {
              inputRef.current?.focus();
            });
          }
        }}
        onMouseDown={() => {
          mouseDownRef.current = true;
        }}
        onMouseUp={() => {
          mouseDownRef.current = false;
        }}
        onPress={() => {
          if (showLoadingIndicator || isAboveOrAtMaxQuantity) {
            // disabling button will cause press to propagate to list item
            // and open item details sheet. So just early return
            return;
          }
          if (isGuestMode && authStackContext) {
            authStackContext.navigateToBecomeCustomer();
            onNavigateToBecomeCustomer?.();
          } else {
            setAutoFocusInput(Platform.OS === 'web');
            incrementQuantity();
          }
        }}
        style={[
          styles.quantityButton,
          isAboveOrAtMaxQuantity ? styles.quantityButtonDisabled : null,
          disabled || incrementDisabled ? styles.quantityButtonDisabled : null,
        ]}
        testID="increment-quantity"
      >
        {loading ? (
          <ActivityIndicator
            color={theme.colors.accentText}
            size="small"
            testID="activity-indicator"
          />
        ) : (
          <PlusIcon
            fill={theme.colors.accentText}
            height={ICON_SIZE}
            width={ICON_SIZE}
          />
        )}
      </TouchableOpacity>
    </>
  );

  return (
    <View style={styles.base}>
      {tooltipText ? (
        <View style={styles.tooltip}>
          <Text
            numberOfLines={1}
            style={[TYPOGRAPHY.xsmall, styles.tooltipText]}
            testID="quantity-tooltip"
          >
            {tooltipText}
          </Text>
        </View>
      ) : null}
      <View style={styles.quantityContainer}>
        {!isOpen && quantityTextString ? (
          <Text
            numberOfLines={3}
            style={[TYPOGRAPHY.xsmall, styles.quantityText, quantityPillStyle]}
            testID="quantity-text"
          >
            {quantityTextString}
          </Text>
        ) : null}

        <View style={styles.wrapper}>
          {variant === QuantitySelectorVariant.BLURRED ? (
            <BlurView
              experimentalBlurMethod="dimezisBlurView"
              intensity={isOpen ? 75 : 0}
              style={[styles.row, styles.blurredRow]}
              tint="light"
            >
              {content}
            </BlurView>
          ) : (
            <View style={styles.row}>{content}</View>
          )}
          {additionalIcon && !isOpen ? (
            <View
              pointerEvents="none"
              style={styles.additionalBorder}
              testID="additional-icon"
            >
              <View style={styles.additionalInner}>
                {React.createElement(additionalIcon, {
                  height: ADDITIONAL_ICON_HEIGHT,
                  stroke: theme.colors.accentText,
                  width: ADDITIONAL_ICON_WIDTH,
                })}
              </View>
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  additionalBorder: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: ADDITIONAL_ITEM_DIMENSION,
    bottom: -3,
    height: ADDITIONAL_ITEM_DIMENSION,
    justifyContent: 'center',
    padding: 2,
    position: 'absolute',
    right: -3,
    width: ADDITIONAL_ITEM_DIMENSION,
  },
  additionalInner: {
    alignItems: 'center',
    backgroundColor: theme.colors.accent,
    borderRadius: ADDITIONAL_ITEM_DIMENSION - 4,
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    height: ORDER_ITEM_QUANTITY_HEIGHT,
    justifyContent: 'center',
    position: 'relative',
  },
  blurredRow: {
    ...Platform.select({
      ios: {
        backgroundColor: `rgba(255, 255, 255, 0.55)`,
      },
    }),
    borderRadius: 40,
    overflow: 'hidden', // Fixing borderRadius with BlurView
    padding: 2,
  },
  container: {
    flexDirection: 'row',
  },
  input: {
    ...FONTS.primaryBold,
    paddingHorizontal: 0,
    textAlign: 'center',
  },
  quantityButton: {
    alignItems: 'center',
    backgroundColor: theme.colors.accent,
    borderRadius: ORDER_ITEM_QUANTITY_HEIGHT,
    height: ORDER_ITEM_QUANTITY_HEIGHT,
    justifyContent: 'center',
    width: ORDER_ITEM_QUANTITY_HEIGHT,
  },
  quantityButtonDisabled: {
    opacity: 0.2,
  },
  quantityContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  quantityText: {
    alignSelf: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: 8,
    color: theme.colors.secondaryText,
    height: '100%',
    justifyContent: 'flex-end',
    lineHeight: 14,
    marginHorizontal: 8,
    maxWidth: 90,
    overflow: 'hidden',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  row: {
    borderRadius: ORDER_ITEM_QUANTITY_HEIGHT,
    flexDirection: 'row',
  },
  textInputContainer: {
    alignSelf: 'auto',
    borderColor: theme.sk.color.border.primary,
    borderRadius: 4,
    borderWidth: 1,
    height: QUANTITY_INPUT_WIDTH,
    marginHorizontal: TEXT_INPUT_CONTAINER_SPACING,
    minHeight: 24,
    minWidth: 24,
    width: QUANTITY_INPUT_WIDTH,
  },
  tooltip: {
    backgroundColor: theme.sk.color.bg.toast,
    borderRadius: 8,
    bottom: ORDER_ITEM_QUANTITY_HEIGHT + 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    position: 'absolute',
  },
  tooltipText: {
    color: theme.colors.white,
    lineHeight: 14,
  },
  wrapper: {
    position: 'relative',
  },
}));
